import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Clock, Package, Truck, Home, Phone, MapPin, ChefHat, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { Logo } from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusSteps = [
  { key: 'pending', label: 'Order Placed', icon: Clock, description: 'Your order has been received' },
  { key: 'preparing', label: 'Preparing', icon: ChefHat, description: 'Our chefs are working on your meal' },
  { key: 'ready', label: 'Out for Delivery', icon: Truck, description: 'Your order is on the way' },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle, description: 'Enjoy your meal!' },
];

// Leaflet map component (loaded dynamically to avoid SSR issues)
function DeliveryMap({ restaurantAddress, customerAddress, status }) {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [MapContainer, setMapContainer] = useState(null);
  const [TileLayer, setTileLayer] = useState(null);
  const [MarkerComp, setMarkerComp] = useState(null);
  const [PopupComp, setPopupComp] = useState(null);
  const [L, setL] = useState(null);

  useEffect(() => {
    // Dynamically import leaflet
    Promise.all([
      import('react-leaflet'),
      import('leaflet'),
      import('leaflet/dist/leaflet.css'),
    ]).then(([rl, leaflet]) => {
      setMapContainer(() => rl.MapContainer);
      setTileLayer(() => rl.TileLayer);
      setMarkerComp(() => rl.Marker);
      setPopupComp(() => rl.Popup);
      setL(leaflet.default);
      setMapLoaded(true);
    }).catch(err => {
      console.error('Failed to load map:', err);
    });
  }, []);

  if (!mapLoaded || !MapContainer || !L) {
    return (
      <div className="h-64 rounded-xl bg-black/50 border border-white/10 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-fire-500" />
      </div>
    );
  }

  // Lagos coordinates (SizzleBox HQ)
  const restaurantPos = [6.5244, 3.3792];
  // Slight offset for delivery location (simulated)
  const customerPos = [6.4400, 3.4200];
  // Rider position depends on status
  const riderPos = status === 'pending' ? restaurantPos
    : status === 'preparing' ? restaurantPos
    : status === 'ready' ? [6.4800, 3.4000]
    : customerPos;

  const center = status === 'ready' ? riderPos : [6.4822, 3.3996];

  // Custom icons
  const restaurantIcon = L.divIcon({
    className: 'custom-marker',
    html: `<div style="background:#FF4500;color:white;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:18px;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);">🏪</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });

  const customerIcon = L.divIcon({
    className: 'custom-marker',
    html: `<div style="background:#10B981;color:white;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:18px;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);">📍</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });

  const riderIcon = L.divIcon({
    className: 'custom-marker',
    html: `<div style="background:#3B82F6;color:white;width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:20px;border:3px solid white;box-shadow:0 2px 12px rgba(59,130,246,0.5);animation:pulse 2s infinite;">🛵</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });

  const Marker = MarkerComp;
  const Popup = PopupComp;

  return (
    <div className="rounded-xl overflow-hidden border border-white/10" data-testid="delivery-map" style={{ height: '280px' }}>
      <style>{`
        .leaflet-container { background: #1a1a1a; }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
      `}</style>
      <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }} zoomControl={false}>
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        <Marker position={restaurantPos} icon={restaurantIcon}>
          <Popup><strong>SizzleBox HQ</strong><br/>{restaurantAddress || 'Lagos, Nigeria'}</Popup>
        </Marker>
        <Marker position={customerPos} icon={customerIcon}>
          <Popup><strong>Delivery Location</strong><br/>{customerAddress || 'Your location'}</Popup>
        </Marker>
        {(status === 'ready') && (
          <Marker position={riderPos} icon={riderIcon}>
            <Popup><strong>Your Rider</strong><br/>On the way!</Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}

export default function OrderTrackingPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const { clearCart } = useCart();

  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(`${API}/payments/status/${sid}`);
      const data = res.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('paid');
        clearCart();
        const orderRes = await axios.get(`${API}/orders/${orderId}`);
        setOrder(orderRes.data);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  }, [orderId, clearCart]);

  useEffect(() => {
    document.title = 'Order Tracking - SizzleBox';
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}`);
        setOrder(res.data);
        
        if (sessionId) {
          pollPaymentStatus(sessionId);
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId, sessionId, pollPaymentStatus]);

  // WebSocket for real-time status updates
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl || !orderId) return;
    let mounted = true;
    let ws = null, reconnectTimeout = null, pingInterval = null;

    const connect = () => {
      if (!mounted) return;
      try { ws = new WebSocket(`${wsUrl}/api/ws`); } catch (_) {
        if (mounted) reconnectTimeout = setTimeout(connect, 3000);
        return;
      }
      ws.onopen = () => {
        pingInterval = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) try { ws.send('ping'); } catch (_) {}
        }, 15000);
      };
      ws.onmessage = (event) => {
        if (!mounted || event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'order_status' && data.order_id === orderId) {
            setOrder(prev => prev ? {
              ...prev,
              status: data.status || prev.status,
              payment_status: data.payment_status || prev.payment_status
            } : prev);
          }
        } catch (_) {}
      };
      ws.onclose = () => {
        clearInterval(pingInterval);
        if (mounted) reconnectTimeout = setTimeout(connect, 2000);
      };
      ws.onerror = () => { clearInterval(pingInterval); ws?.close(); };
    };
    connect();
    return () => { mounted = false; clearTimeout(reconnectTimeout); clearInterval(pingInterval); if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) ws.close(); };
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-black pt-28">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-3xl text-white mb-4">Order not found</h1>
          <Link to="/">
            <Button className="bg-fire-500 hover:bg-fire-600 text-white rounded-full px-8">
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentStepIndex = statusSteps.findIndex(s => s.key === order.status);
  const estimatedTime = order.status === 'pending' ? '35-45 min'
    : order.status === 'preparing' ? '20-30 min'
    : order.status === 'ready' ? '10-15 min'
    : 'Arrived!';

  return (
    <div className="min-h-screen bg-black" data-testid="order-tracking-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <Logo className="h-12" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-28 pb-20">
        <div className="container mx-auto px-6 max-w-3xl">
          {/* Payment Status Banner */}
          {sessionId && (
            <div className={`mb-8 p-4 rounded-xl ${
              paymentStatus === 'paid' ? 'bg-green-500/20 border border-green-500/50' :
              paymentStatus === 'processing' ? 'bg-yellow-500/20 border border-yellow-500/50' :
              paymentStatus === 'error' || paymentStatus === 'expired' ? 'bg-red-500/20 border border-red-500/50' :
              'bg-gray-500/20 border border-gray-500/50'
            }`}>
              <p className={`text-center font-semibold ${
                paymentStatus === 'paid' ? 'text-green-400' :
                paymentStatus === 'processing' ? 'text-yellow-400' :
                paymentStatus === 'error' || paymentStatus === 'expired' ? 'text-red-400' :
                'text-gray-400'
              }`}>
                {paymentStatus === 'paid' && 'Payment Successful!'}
                {paymentStatus === 'processing' && 'Verifying payment...'}
                {paymentStatus === 'error' && 'Payment verification failed'}
                {paymentStatus === 'expired' && 'Payment session expired'}
                {paymentStatus === 'timeout' && 'Payment status unknown - please check your email'}
              </p>
            </div>
          )}

          {/* Order Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-fire-500/20 rounded-full mb-4">
              {order.status === 'delivered' ? (
                <CheckCircle className="w-10 h-10 text-green-400" />
              ) : (
                <Package className="w-10 h-10 text-fire-500" />
              )}
            </div>
            <h1 className="font-heading text-4xl text-white mb-1">
              {order.status === 'delivered' ? 'Order Delivered!' : 'Order Confirmed!'}
            </h1>
            <p className="text-gray-400">Order #{order.order_number}</p>
            {order.status !== 'delivered' && (
              <div className="mt-3 inline-flex items-center gap-2 bg-fire-500/10 border border-fire-500/30 rounded-full px-4 py-2">
                <Clock className="w-4 h-4 text-fire-500" />
                <span className="text-fire-500 font-semibold text-sm">Est. delivery: {estimatedTime}</span>
              </div>
            )}
          </div>

          {/* Delivery Map */}
          <div className="mb-8" data-testid="map-section">
            <DeliveryMap
              restaurantAddress="Lagos, Nigeria"
              customerAddress={order.customer_address}
              status={order.status}
            />
          </div>

          {/* Status Timeline */}
          <div className="bg-card rounded-2xl p-6 border border-white/10 mb-8">
            <h2 className="text-white font-bold text-xl mb-6">Order Status</h2>
            
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-white/10" />
              <div
                className="absolute left-6 top-0 w-0.5 bg-fire-500 transition-all duration-500"
                style={{ height: `${(currentStepIndex / (statusSteps.length - 1)) * 100}%` }}
              />

              {/* Steps */}
              <div className="space-y-8">
                {statusSteps.map((step, index) => {
                  const isCompleted = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;
                  const Icon = step.icon;

                  return (
                    <div key={step.key} className="flex items-center gap-4 relative" data-testid={`status-step-${step.key}`}>
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 transition-all ${
                        isCompleted ? 'bg-fire-500 shadow-[0_0_12px_rgba(255,69,0,0.3)]' : 'bg-card border border-white/20'
                      } ${isCurrent && order.status !== 'delivered' ? 'ring-2 ring-fire-500/50 ring-offset-2 ring-offset-black' : ''}`}>
                        <Icon className={`w-6 h-6 ${isCompleted ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <p className={`font-bold ${isCompleted ? 'text-white' : 'text-gray-500'}`}>
                          {step.label}
                        </p>
                        <p className={`text-sm ${isCurrent ? 'text-fire-500' : 'text-gray-500'}`}>
                          {isCurrent && order.status !== 'delivered' ? step.description : (isCompleted ? 'Completed' : 'Pending')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Order Details */}
          <div className="bg-card rounded-2xl p-6 border border-white/10 mb-8">
            <h2 className="text-white font-bold text-xl mb-6">Order Details</h2>
            
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400">{item.quantity}x</span>
                    <span className="text-white">{item.name}</span>
                  </div>
                  <span className="text-white">₦{(item.price * item.quantity).toLocaleString()}</span>
                </div>
              ))}
              
              <div className="border-t border-white/10 pt-4 mt-4 space-y-2">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal</span>
                  <span className="text-white">₦{order.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Delivery</span>
                  <span className="text-white">₦{order.delivery_fee.toLocaleString()}</span>
                </div>
                {(order.discount > 0) && (
                  <div className="flex justify-between text-green-400">
                    <span>Discount {order.promo_code && <span className="text-xs font-mono">({order.promo_code})</span>}</span>
                    <span>-₦{order.discount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-white/10">
                  <span className="text-white font-bold">Total</span>
                  <span className="text-fire-500 font-bold text-xl">₦{order.total.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-card rounded-2xl p-6 border border-white/10">
            <h2 className="text-white font-bold text-xl mb-6">Delivery Information</h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-400">
                <Home className="w-5 h-5" />
                <span className="text-white">{order.customer_name}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Phone className="w-5 h-5" />
                <span className="text-white">{order.customer_phone}</span>
              </div>
              {order.customer_address && (
                <div className="flex items-center gap-3 text-gray-400">
                  <MapPin className="w-5 h-5" />
                  <span className="text-white">{order.customer_address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`${API}/orders/receipt/${orderId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white rounded-full px-8" data-testid="download-receipt-btn">
                <FileText className="w-4 h-4 mr-2" />View Receipt
              </Button>
            </a>
            <Link to="/menu">
              <Button className="w-full sm:w-auto bg-fire-500 hover:bg-fire-600 text-white rounded-full px-8">
                Order Again
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 rounded-full px-8">
                Go Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
